import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, Select, MenuItem,
  FormControl, InputLabel, InputAdornment, Chip, CircularProgress,
  Tooltip, TablePagination, Stack, useMediaQuery, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getAuditLog } from '../services/api';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const ACTION_COLORS = {
  CREAR: 'success',
  EDITAR: 'warning',
  ELIMINAR: 'error',
  LOGIN: 'default',
};

const ACCIONES = ['CREAR', 'EDITAR', 'ELIMINAR', 'LOGIN'];

// Traducción de los nombres de clase Python que el backend registra en
// AuditLog.entidad (ver registrar() en backend/audit.py, llamado desde
// cada router con el nombre de la clase tal cual) — antes se mostraban
// sin traducir ("Patient", "Appointment"...) y el filtro de texto pedía
// escribir ese mismo nombre en inglés. Reportado por el usuario
// (2026-10-20). Un valor no mapeado (ej. una entidad nueva agregada a
// futuro sin actualizar este diccionario) se muestra tal cual como
// respaldo, nunca se oculta ni rompe.
const ENTIDAD_LABELS = {
  Patient: 'Paciente',
  Appointment: 'Turno',
  AppointmentType: 'Tipo de turno',
  Doctor: 'Médico',
  DoctorSchedule: 'Horario de médico',
  User: 'Usuario',
  Clinic: 'Clínica',
  ClinicFeatures: 'Módulos de la clínica',
  ClinicDicomApiKey: 'Clave DICOM de la clínica',
  ClinicExport: 'Exportación de la clínica',
  MedicalRecord: 'Historia clínica',
  MedicalRecordFile: 'Archivo de historia clínica',
  PatientFile: 'Archivo de paciente',
  PatientInsurance: 'Cobertura del paciente',
  PatientProblem: 'Problema del paciente',
  Prescription: 'Receta',
  StudyOrder: 'Orden de estudio',
  StudyOrderTemplate: 'Plantilla de orden de estudio',
  ConsultationPayment: 'Pago',
  ConsultationTemplate: 'Plantilla de consulta',
  ConsultationType: 'Tipo de consulta',
  WhatsAppTemplate: 'Plantilla de WhatsApp',
  EmailTemplate: 'Plantilla de email',
  DecisionTree: 'Algoritmo',
  DecisionNode: 'Nodo de algoritmo',
  DecisionNodeMedia: 'Imagen de algoritmo',
  DecisionOption: 'Opción de algoritmo',
  WaitlistEntry: 'Lista de espera',
  SelfUpdateLink: 'Link de autocompletado',
};
const entidadLabel = (valor) => ENTIDAD_LABELS[valor] || valor;
const ENTIDAD_OPTIONS = Object.keys(ENTIDAD_LABELS).sort((a, b) => ENTIDAD_LABELS[a].localeCompare(ENTIDAD_LABELS[b]));

const Audit = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    q: '', accion: '', entidad: '', fecha_desde: '', fecha_hasta: '',
  });
  const searchTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * rowsPerPage, limit: rowsPerPage };
      if (filters.q) params.q = filters.q;
      if (filters.accion) params.accion = filters.accion;
      if (filters.entidad) params.entidad = filters.entidad;
      if (filters.fecha_desde) params.fecha_desde = filters.fecha_desde;
      if (filters.fecha_hasta) params.fecha_hasta = filters.fecha_hasta;
      const res = await getAuditLog(params);
      setRows(res.data?.items || []);
      setTotal(res.data?.total ?? 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (field) => (e) => {
    setPage(0);
    setFilters(prev => ({ ...prev, [field]: e.target.value }));
  };

  // El buscador de texto dispara una consulta al servidor por cada cambio —
  // a diferencia de Algoritmos (client-side), acá sí hace falta debounce
  // para no spamear al backend en cada tecla presionada.
  const handleSearchChange = (e) => {
    const val = e.target.value;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(0);
      setFilters(prev => ({ ...prev, q: val }));
    }, 350);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Auditoría</Typography>

      {/* Filtros — en mobile cada control va en su propia fila a ancho
          completo; los 5 campos en una sola fila con flexWrap se apilaban
          de forma impredecible en pantallas angostas. */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', sm: 'center' }, flexWrap: { sm: 'wrap' } }}>
        <TextField
          size="small"
          placeholder="Buscar por usuario o detalle…"
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} /></InputAdornment>,
          }}
          sx={{ flex: 1, minWidth: { sm: 220 } }}
        />

        <FormControl size="small" sx={{ minWidth: { sm: 150 } }}>
          <InputLabel>Acción</InputLabel>
          <Select value={filters.accion} label="Acción" onChange={setFilter('accion')}>
            <MenuItem value=""><em>Todas</em></MenuItem>
            {ACCIONES.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { sm: 180 } }}>
          <InputLabel>Entidad</InputLabel>
          <Select value={filters.entidad} label="Entidad" onChange={setFilter('entidad')}>
            <MenuItem value=""><em>Todas</em></MenuItem>
            {ENTIDAD_OPTIONS.map(e => <MenuItem key={e} value={e}>{ENTIDAD_LABELS[e]}</MenuItem>)}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }}
            value={filters.fecha_desde} onChange={setFilter('fecha_desde')} sx={{ flex: { xs: 1, sm: 'initial' } }} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }}
            value={filters.fecha_hasta} onChange={setFilter('fecha_hasta')} sx={{ flex: { xs: 1, sm: 'initial' } }} />
        </Box>
      </Paper>

      {/* fecha viene naive-UTC del backend — agregar la 'Z' evita que el
          navegador lo interprete como hora local */}
      {(() => {
        const formatFecha = (fecha) => new Date(fecha.endsWith('Z') ? fecha : fecha + 'Z').toLocaleString('es-AR', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        });

        if (loading) {
          return (
            <Paper sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Paper>
          );
        }
        if (rows.length === 0) {
          return (
            <Paper sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              Sin registros
            </Paper>
          );
        }

        // La tabla de 7 columnas no entra en pantallas chicas — en mobile
        // se reemplaza por tarjetas apiladas, mismo criterio que Patients.js.
        if (isMobile) {
          return (
            <Stack spacing={1.5}>
              {rows.map(r => (
                <Paper key={r.id} sx={{ p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary">{formatFecha(r.fecha)}</Typography>
                    <Chip label={r.accion} color={ACTION_COLORS[r.accion] || 'default'} size="small" />
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{entidadLabel(r.entidad)}{r.entidad_id != null ? ` #${r.entidad_id}` : ''}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, mb: r.detalle ? 0.75 : 0 }}>
                    <Typography variant="caption" color="text.secondary">{r.username}</Typography>
                    <Chip label={r.user_role} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                  </Box>
                  {r.detalle && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                      {r.detalle}
                    </Typography>
                  )}
                </Paper>
              ))}
              <TablePagination
                component={Paper}
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                labelRowsPerPage="Filas:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            </Stack>
          );
        }

        return (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Usuario</TableCell>
                    <TableCell>Rol</TableCell>
                    <TableCell>Acción</TableCell>
                    <TableCell>Entidad</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Detalle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                        {formatFecha(r.fecha)}
                      </TableCell>
                      <TableCell>{r.username}</TableCell>
                      <TableCell>
                        <Chip label={r.user_role} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.accion}
                          color={ACTION_COLORS[r.accion] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{entidadLabel(r.entidad)}</TableCell>
                      <TableCell>{r.entidad_id ?? '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {r.detalle ? (
                          <Tooltip title={r.detalle} arrow>
                            <Typography variant="caption" noWrap sx={{ display: 'block', maxWidth: 240 }}>
                              {r.detalle}
                            </Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage="Filas:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </Paper>
        );
      })()}
    </Box>
  );
};

export default Audit;
